import { useCallback, useMemo, useState } from 'react'
import { object, SchemaOf, string } from 'yup'
import { Map } from 'immutable'
import { useSelector } from 'react-redux'
import { selectBrooklynServerUrl } from '../../config/redux/selectors'
import { selectAuth } from '../../auth/redux/selectors'
import { BRIGHTCOVE_YOGA_INTERNATIONAL_PROVIDER } from '../constants'
import { Result } from '../Result'
import { MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_MEDIA_STREAM } from '../../media-assets/reference-types'
import { LANGUAGE_EN, LANGUAGE_ES } from '../../languages/constants'
import { upsertMediaAsset } from '../../media-assets'

type AssetParams = {
  mediaId: string
  mediaReferenceType: string
  language: string
  provider: string
  // brightcove id
  providerKey: string
  mimeType: string
  originalFileName: string
}

export type MediaDetails = {
  mediaId: string
}
type FormValues = Pick<AssetParams, 'language' | 'providerKey'>

const paramsValidationSchema: SchemaOf<AssetParams> = object({
  mediaId: string().required(),
  mediaReferenceType: string()
    .required()
    .matches(new RegExp(`${MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_MEDIA_STREAM}`)),
  language: string()
    .required()
    .matches(new RegExp(`(${LANGUAGE_EN}|${LANGUAGE_ES})`)),
  provider: string()
    .required()
    .matches(new RegExp(`${BRIGHTCOVE_YOGA_INTERNATIONAL_PROVIDER}`)),
  // brightcove id
  providerKey: string().required().required(),
  mimeType: string()
    .required()
    .matches(/application\/x-mpegurl/),
  originalFileName: string().required().required(),
})

function convertToParams (values: FormValues, mediaDetails: MediaDetails): AssetParams {
  const params = {
    ...values,
    mediaId: mediaDetails.mediaId,
    // YI currently uses only Media Steam
    mediaReferenceType: MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_MEDIA_STREAM,
    provider: BRIGHTCOVE_YOGA_INTERNATIONAL_PROVIDER,
    mimeType: 'application/x-mpegurl' as const,
    // origin filename template copied from migration script,
    // Note: the value doesn't affect on anything
    originalFileName: `BC_ID_${values.providerKey}`,
  }
  return params
}

export function useCreateMediaAsset (
  { media }: { media: MediaDetails },
  { onSuccess }: { onSuccess?: () => void } = {},
) {
  const authDetails = useSelector<unknown, Map<string, unknown>>(selectAuth)
  const { isLoading: isConfigLoading, url: brooklynUrl } = useSelector(selectBrooklynServerUrl)
  const [isCreating, setIsCreating] = useState(false)

  const [error, setError] = useState<Error | string | null>(null)

  const create = useCallback(
    async (values: FormValues) => {
      let createAssetResult: Map<string, unknown> | null = null

      const params = convertToParams(values, media)

      const paramsOrError = Result.encase(() => paramsValidationSchema.validateSync(params))()

      if (paramsOrError.status === 'fail') {
        setError(paramsOrError.error)
        return paramsOrError
      }

      try {
        setIsCreating(true)
        createAssetResult = await upsertMediaAsset({
          auth: authDetails,
          server: brooklynUrl,
          data: Map(paramsOrError.result),
        }).then((response) => {
          if (typeof onSuccess === 'function') {
            onSuccess()
          }
          return response
        })
      } catch (e) {
        const errorMsg = typeof e === 'string' || e instanceof Error ? e : 'unknonw type of error'
        setError(errorMsg)
        setIsCreating(false)

        return Result.fail(errorMsg)
      }
      if (createAssetResult === null) {
        const errorMsg = 'media upsert failed'
        setError(errorMsg)

        setIsCreating(false)
        return Result.fail(errorMsg)
      }

      setIsCreating(false)
      return Result.ok(createAssetResult)
    },
    [authDetails, brooklynUrl, media, onSuccess],
  )

  const loading = isCreating || isConfigLoading

  const tuple = useMemo(() => [create, { loading, error }] as const, [create, loading, error])
  return tuple
}
